import type { DegapCycle } from '@/types/prioritization/degapCycle';
import type { Track } from '@/types/prioritization/track';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import type {
  CatchballLevelConfig,
  CatchballLoop,
  LoopIntent,
} from '@/types/prioritization/catchballLoop';
import type { CatchballParticipantRef } from '@/types/prioritization/catchballVirtualConfig';
import type { CycleDesignerFormState, DesignerPath, OrgPathNode, PathMode } from '../types';
import { launchCycleDesign, type GovernedResult } from '@/lib/governedTransactions';
import { DESIGNER_LAUNCHED_AT_KEY, mergeDesignerLaunchedMetadata } from './cycleDesignerPhase';
import { runPreLaunchChecks } from './validation';
import { pathEndpointOrgUnitId, pathToLevelConfig } from './pathToLevelConfig';
import { buildOrgById, pathChildrenOf, pathParentOf, withInheritedOwners } from './pathBuilder';
import {
  CUSTOM_ANCHOR_ID,
  assignCustomRung,
  customPathParticipants,
  isCustomNodeId,
  pathModeOf,
} from './customPathModel';
import {
  buildCadenceSchedule,
  firstTossDueDate,
  pathCadenceFor,
  type PathCadence,
} from './cadenceSchedule';

export const DESIGNER_COLOR_METADATA_KEY = 'designerColor';
export const DESIGNER_PATH_ORG_UNITS_KEY = 'designerPathOrgUnitIds';
/**
 * Which stop each stop hangs off (stop id → the stop above it on the path).
 * A path may fork, so the ordered list above is not enough to know what is
 * below what once the path is running.
 */
export const DESIGNER_PATH_PARENTS_KEY = 'designerPathParents';
export const DESIGNER_TRACK_PARAMS_KEY = 'designerTrackParams';
export const DESIGNER_PATH_SLOT_KEY = 'designerPathSlot';
export const DESIGNER_PATH_ASSIGNMENTS_KEY = 'designerPathAssignments';
/** Editable per-node response-window overrides (org unit id → days). */
export const DESIGNER_PATH_WINDOWS_KEY = 'designerPathWindows';
export const CATCHBALL_LEVEL_CONFIG_METADATA_KEY = 'catchballLevelConfig';
/** T-minus machinery (2026-07-18): the full computed calendar for the path. */
export const DESIGNER_CADENCE_SCHEDULE_KEY = 'designerCadenceSchedule';

/** A per-step writer the launch no longer calls (see `LaunchCycleDesignerInput.updateCycle`). */
type DeprecatedLaunchStep = (...args: never[]) => unknown;

export interface LaunchCycleDesignerInput {
  cycle: DegapCycle;
  form: CycleDesignerFormState;
  paths: DesignerPath[];
  orgNodes: OrgPathNode[];
  existingTracks: Track[];
  existingLoops: CatchballLoop[];
  orgById: Map<string, OrgPathNode>;
  userId?: string;
  /** Undefined means 'org_chart' — how this design names its stops. */
  pathMode?: PathMode;
  workspaceId?: string | null;
  /**
   * The cycle owner, pinned to a custom path's anchor. Canon 29 makes the owner
   * the executive tier, and naming them explicitly keeps every custom stop
   * assigned — an unassigned stop would send recipient resolution looking for
   * the synthetic anchor id in `user_node_assignments.org_unit_id`, a uuid
   * column that rejects it outright.
   */
  ownerRef?: CatchballParticipantRef;
  /**
   * The governed launch RPC. Defaults to `launchCycleDesign`
   * (`launch_cycle_design`, migration 749); tests inject a fake. When injected,
   * the post-launch store refresh is skipped.
   */
  launchDesign?: (cycleId: string, config: Record<string, unknown>) => Promise<GovernedResult>;
  /**
   * @deprecated Since migration 749 the launch is one server transaction; the
   * old per-step writers (updateCycle, createTrack, updateTrack, archiveTrack,
   * createLoop, updateLoopIntent, saveVirtualConfig) are no longer called.
   * Accepted and ignored so existing callers compile.
   */
  updateCycle?: DeprecatedLaunchStep;
  /** @deprecated see `updateCycle`. */
  createTrack?: DeprecatedLaunchStep;
  /** @deprecated see `updateCycle`. */
  archiveTrack?: DeprecatedLaunchStep;
  /** @deprecated see `updateCycle`. */
  updateTrack?: DeprecatedLaunchStep;
  /** @deprecated see `updateCycle`. */
  createLoop?: DeprecatedLaunchStep;
  /** @deprecated see `updateCycle`. */
  updateLoopIntent?: DeprecatedLaunchStep;
  /** @deprecated see `updateCycle`. */
  saveVirtualConfig?: DeprecatedLaunchStep;
}

export interface LaunchCycleDesignerResult {
  ok: boolean;
  error?: string;
  createdTrackIds: string[];
  firstTrackId?: string;
}

/** Tracks eligible for cleanup after designer creates named paths. */
export function defaultTracksToArchive(
  existingTracks: Track[],
  existingLoops: CatchballLoop[],
  createdTrackIds: string[],
): Track[] {
  const loopTrackIds = new Set(existingLoops.map((l) => l.trackId));
  return existingTracks.filter(
    (t) =>
      !createdTrackIds.includes(t.id) &&
      !loopTrackIds.has(t.id) &&
      t.status !== 'archived',
  );
}

export function buildCycleMetadataPatch(
  cycle: DegapCycle,
  form: CycleDesignerFormState,
  launchedAt: string,
  paths: DesignerPath[],
): Record<string, unknown> {
  return mergeDesignerLaunchedMetadata(cycle, launchedAt, {
    strategicIntent: form.strategicIntent.trim(),
    cycleDesignerType: form.cycleType,
    maxIterations: form.maxIterations,
    designerDateRange: { start: form.startDate, end: form.endDate },
    designerFrameCount: paths.length,
    // Survives the draft being stripped below, so reopening a launched design
    // rebuilds it in the mode it was drawn in rather than resetting its stops.
    designerPathMode: pathModeOf(form),
    // The autosaved draft has served its purpose once the design launches.
    designerDraft: null,
    // So does the saved launch payload — cleared by the launch transaction
    // itself, whichever client (web or iOS) submitted it.
    [LAUNCH_PAYLOAD_METADATA_KEY]: null,
  });
}

export function buildTrackMetadata(
  path: DesignerPath,
  orgNodes: OrgPathNode[],
  /** The path's computed calendar; persisted so post-launch surfaces can read the plan. */
  cadence?: PathCadence | null,
  pathMode?: PathMode,
): Record<string, unknown> {
  const levelConfig = pathToLevelConfig(path, orgNodes, path.params, cadence);
  return {
    [DESIGNER_COLOR_METADATA_KEY]: path.color,
    [DESIGNER_PATH_ORG_UNITS_KEY]: path.nodes,
    [DESIGNER_PATH_PARENTS_KEY]: pathParentMap(path, orgNodes),
    [DESIGNER_TRACK_PARAMS_KEY]: path.params,
    [DESIGNER_PATH_SLOT_KEY]: path.id,
    [DESIGNER_PATH_ASSIGNMENTS_KEY]: path.nodeAssignments ?? null,
    // The editable overrides themselves — the cadence schedule below already
    // bakes them into due dates, but reopening the Designer needs the raw
    // per-node values back to keep them editable.
    [DESIGNER_PATH_WINDOWS_KEY]: path.nodeWindows ?? null,
    [CATCHBALL_LEVEL_CONFIG_METADATA_KEY]: levelConfig,
    [DESIGNER_CADENCE_SCHEDULE_KEY]: cadence ?? null,
    // A custom path's stops are named recipients, not org units, and
    // catchball_adjust_path validates every stop against `org_units` — so the
    // adjust affordance cannot operate on this track. Track layer wins the
    // routing merge (canon 29 §6), leaving org-chart tracks untouched.
    ...(pathMode === 'custom' ? { catchballRouting: { pathAdjustPolicy: 'none' } } : {}),
  };
}

/** Find the track a path already created (relaunch after reopen) — by slot, then name. */
export function findExistingTrackForPath(
  path: DesignerPath,
  existingTracks: Track[],
): Track | undefined {
  const live = existingTracks.filter((t) => t.status !== 'archived');
  return (
    live.find(
      (t) => (t.metadata as Record<string, unknown> | null)?.[DESIGNER_PATH_SLOT_KEY] === path.id,
    ) ?? live.find((t) => t.name === path.name)
  );
}

/** Stop id → the stop above it on the path, for every stop below the top. */
export function pathParentMap(path: DesignerPath, orgNodes: OrgPathNode[]): Record<string, string> {
  const orgById = buildOrgById(orgNodes);
  const out: Record<string, string> = {};
  for (const nodeId of path.nodes) {
    const parent = pathParentOf(path, nodeId, orgById);
    if (parent) out[nodeId] = parent;
  }
  return out;
}

/** The first request's prefill: everything the Designer already knows. */
export function buildSeedIntent(
  form: CycleDesignerFormState,
  path: DesignerPath,
  /** The path's computed calendar; the first toss carries level 1's due date. */
  cadence?: PathCadence | null,
  pathMode?: PathMode,
  /** The org tree the path was drawn on; needed to see a fork below the anchor. */
  orgNodes?: OrgPathNode[],
): Partial<LoopIntent> {
  return {
    // The Path's identity, so every downstream surface can say WHICH route a
    // Loop is running over — not just which org units it passes through.
    pathSlotId: path.id,
    pathName: path.name,
    strategicContext: form.strategicIntent.trim(),
    responseGuidance: path.params.guidance.trim() || undefined,
    // T-minus machinery: the first toss is due when LEVEL 1's window closes —
    // not at the end of the whole cycle. Falls back to the cycle end date when
    // the design has no computable calendar.
    dueDate: firstTossDueDate(cadence ?? null) ?? (form.endDate || undefined),
    maxIterations: null,
    expectedResponseType: path.params.responseType,
    // The ball descends ONE level at a time (catchball dance, Doc 120): the
    // first toss goes ONLY to the next stop below the anchor — never the whole
    // path at once. Each subsequent handoff happens at exchange time.
    //
    // Custom stops carry their recipient in the level config instead. Their ids
    // name no org unit, so listing them here would only surface a raw synthetic
    // id as a recipient label in the Composer.
    //
    // A path that forks directly below the anchor has several next stops; the
    // first toss goes to each of them (owner ruling 2026-10-13: selecting more
    // than one stop in the row below is the fork).
    recipientOrgUnitIds:
      pathMode === 'custom' || path.nodes.length <= 1
        ? []
        : pathChildrenOf(path, path.nodes[0], buildOrgById(orgNodes ?? [])),
  };
}

/** One path, as `launch_cycle_design` (migration 749) takes it. */
export interface LaunchPathConfig {
  path_id: string;
  path_name: string;
  /** Relaunch: the track this path already created; null creates one. */
  existing_track_id: string | null;
  track: {
    name: string;
    description?: string;
    /** 'org_unit' or 'default'; the server turns a second unscoped track into 'custom_group'. */
    scope_type: string;
    scope_entity_id?: string;
    scope_label: string;
    metadata: Record<string, unknown>;
  };
  level_config: CatchballLevelConfig;
  seed_intent: Partial<LoopIntent>;
}

/** The whole launch, as one server transaction takes it. */
export interface LaunchCycleDesignConfig {
  cycle: { name: string; description: string; metadata_patch: Record<string, unknown> };
  path_mode: PathMode;
  virtual_config: {
    setup_mode: 'org_chart' | 'virtual';
    ball_recipients: CatchballParticipantRef[];
    default_participants: CatchballParticipantRef[];
  };
  paths: LaunchPathConfig[];
  archive_track_ids: string[];
  originator: { org_unit_id: string; org_unit_name: string | null } | null;
}

/**
 * Everything the launch will write, computed without writing anything.
 * Pure: the server applies it in one transaction (`launch_cycle_design`).
 */
export function buildLaunchConfig(
  input: Pick<
    LaunchCycleDesignerInput,
    'cycle' | 'form' | 'paths' | 'orgNodes' | 'existingTracks' | 'existingLoops' | 'orgById' | 'pathMode' | 'ownerRef'
  > & { originatorOrgUnitName?: string | null },
): { ok: true; config: LaunchCycleDesignConfig } | { ok: false; error: string } {
  const { cycle, form, orgNodes, existingTracks, existingLoops, pathMode } = input;
  const orgById = input.orgById.size > 0 ? input.orgById : buildOrgById(orgNodes);

  // Custom anchors carry the cycle owner (see `ownerRef`); org-chart anchors are
  // real org units and resolve on their own.
  // A unit with no owner of its own goes to the owner above it, and at the top
  // to the cycle's owner (owner ruling 2026-11-03), so every stop names who
  // answers for it.
  const paths =
    pathMode === 'custom'
      ? input.ownerRef
        ? input.paths.map((p) => assignCustomRung(p, CUSTOM_ANCHOR_ID, input.ownerRef!))
        : input.paths
      : input.paths.map((p) => withInheritedOwners(p, input.paths, orgById, input.ownerRef ?? null));

  if (paths.length === 0) {
    return { ok: false, error: 'At least one ball path is required.' };
  }

  const launchedAt = new Date().toISOString();
  // The calendar the design implies — computed once, persisted per path
  // (track metadata + level config) and carried by the first toss's due date.
  const cadenceSchedule = buildCadenceSchedule(form, paths, orgNodes);

  const reusedTrackIds: string[] = [];
  const pathConfigs: LaunchPathConfig[] = paths.map((path) => {
    const endpointId = pathEndpointOrgUnitId(path, orgById);
    const endpoint = endpointId ? orgById.get(endpointId) : undefined;
    const scopeLabel = endpoint?.name ?? path.name;
    // A Path is the route; the Track is the lane it creates at the route's
    // endpoint. The track takes its endpoint stop's name ("Cloud Servers") and
    // only falls back to the path name when a path has no endpoint beyond the
    // anchor (owner ruling 2026-07-27).
    const trackName = scopeLabel;
    const cadence = pathCadenceFor(cadenceSchedule, path.id);
    // `tracks.scope_entity_id` is a uuid column, so a custom stop's synthetic id
    // must never reach it — such a track is simply not org-scoped.
    const endpointIsOrgUnit = Boolean(endpointId) && !isCustomNodeId(endpointId!);
    // Relaunch after reopen must not duplicate tracks: reuse the path's track.
    const existing = findExistingTrackForPath(path, existingTracks);
    if (existing) reusedTrackIds.push(existing.id);
    return {
      path_id: path.id,
      path_name: path.name,
      existing_track_id: existing?.id ?? null,
      track: {
        name: trackName,
        description: `Track for ${path.name}`,
        scope_type: endpointIsOrgUnit ? 'org_unit' : 'default',
        scope_entity_id: endpointIsOrgUnit ? endpointId : undefined,
        scope_label: scopeLabel,
        metadata: buildTrackMetadata(path, orgNodes, cadence, pathMode),
      },
      // A5 (owner ruling 2026-07-08): launch kicks off the exchange — a loop per
      // track, its intent pre-seeded so the Request Composer opens ready to send.
      level_config: pathToLevelConfig(path, orgNodes, path.params, cadence),
      seed_intent: buildSeedIntent(form, path, cadence, pathMode, orgNodes),
    };
  });

  // Canon 29 §2.1 participant set. Written for both modes so relaunching an
  // org-chart design clears a stale virtual config that would otherwise keep
  // hijacking participant resolution (`_catchball_actor_eligible` only takes its
  // named-recipient branch for `setup_mode = 'virtual'`).
  const isCustom = pathMode === 'custom';
  const participants = customPathParticipants(paths);

  const originatorId = cycle.orgScopeNodeId ?? null;

  return {
    ok: true,
    config: {
      cycle: {
        name: form.cycleName.trim() || cycle.name,
        description: form.strategicIntent.trim() || (cycle.description ?? ''),
        metadata_patch: buildCycleMetadataPatch(cycle, form, launchedAt, paths),
      },
      path_mode: pathMode ?? 'org_chart',
      virtual_config: {
        setup_mode: isCustom ? 'virtual' : 'org_chart',
        ball_recipients: isCustom ? participants.ballRecipients : [],
        default_participants: isCustom ? participants.defaultParticipants : [],
      },
      paths: pathConfigs,
      archive_track_ids: defaultTracksToArchive(existingTracks, existingLoops, reusedTrackIds).map((t) => t.id),
      originator: originatorId
        ? { org_unit_id: originatorId, org_unit_name: input.originatorOrgUnitName ?? null }
        : null,
    },
  };
}

/**
 * Owner ruling #8, Option A (2026-09-22): a complete design is saved on the
 * cycle as the exact `launch_cycle_design` payload, so iOS can launch it
 * without re-implementing the Designer. Written by the draft autosave next to
 * `designerDraft`; cleared when the design becomes incomplete and by the launch
 * transaction itself (`buildCycleMetadataPatch`).
 */
export const LAUNCH_PAYLOAD_METADATA_KEY = 'launchPayload';

/**
 * What the saved payload depends on beyond the design itself. iOS compares it
 * with the live rows before submitting; the server re-checks tracks on its own.
 */
export interface LaunchPayloadFingerprint {
  /** Every track of the cycle when the payload was saved. */
  tracks: { id: string; status: string; updatedAt: string | null }[];
  /** Org units the design routes through (org-chart mode), sorted. */
  orgUnitIds: string[];
  /** People and groups named on stops, sorted `type:id`. */
  participants: string[];
}

export interface StoredLaunchPayload {
  /** The exact `p_config` web would send to `launch_cycle_design`. */
  config: LaunchCycleDesignConfig;
  savedAt: string;
  fingerprint: LaunchPayloadFingerprint;
}

function isoOrNull(value: unknown): string | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value.toISOString();
  return typeof value === 'string' && value ? value : null;
}

/**
 * The launch payload for a complete design, or null when the Designer's own
 * pre-launch checks would keep its Launch button disabled. Reuses
 * `buildLaunchConfig` — the same payload web submits — with one difference: the
 * metadata patch carries only the launch keys, never a copy of the cycle's
 * whole metadata (which would nest earlier payloads and, submitted later,
 * overwrite metadata written since).
 */
export function buildStoredLaunchPayload(
  input: Pick<
    LaunchCycleDesignerInput,
    'cycle' | 'form' | 'paths' | 'orgNodes' | 'existingTracks' | 'existingLoops' | 'orgById' | 'pathMode' | 'ownerRef'
  > & { originatorOrgUnitName?: string | null },
  savedAt: string = new Date().toISOString(),
): StoredLaunchPayload | null {
  if (!runPreLaunchChecks(input.paths, input.orgNodes, input.pathMode).allHardPass) return null;
  const built = buildLaunchConfig({ ...input, cycle: { ...input.cycle, metadata: {} } });
  if (!built.ok) return null;

  const orgUnitIds = new Set<string>();
  const participants = new Set<string>();
  for (const path of input.paths) {
    if (input.pathMode !== 'custom') {
      for (const node of path.nodes) if (!isCustomNodeId(node)) orgUnitIds.add(node);
    }
    for (const ref of Object.values(path.nodeAssignments ?? {})) participants.add(`${ref.type}:${ref.id}`);
  }
  if (input.pathMode === 'custom' && input.ownerRef) {
    participants.add(`${input.ownerRef.type}:${input.ownerRef.id}`);
  }

  return {
    config: built.config,
    savedAt,
    fingerprint: {
      tracks: input.existingTracks
        .map((t) => ({ id: t.id, status: t.status, updatedAt: isoOrNull(t.updatedAt) }))
        .sort((a, b) => a.id.localeCompare(b.id)),
      orgUnitIds: [...orgUnitIds].sort(),
      participants: [...participants].sort(),
    },
  };
}

/**
 * Change-detection key for a stored payload: everything but its timestamps
 * (`savedAt` and the launch time baked into the metadata patch).
 */
export function launchPayloadKey(payload: StoredLaunchPayload | null | undefined): string {
  if (!payload || typeof payload !== 'object' || !payload.config) return 'none';
  const patch = { ...(payload.config.cycle?.metadata_patch ?? {}) };
  delete patch[DESIGNER_LAUNCHED_AT_KEY];
  return JSON.stringify({
    config: { ...payload.config, cycle: { ...payload.config.cycle, metadata_patch: patch } },
    fingerprint: payload.fingerprint,
  });
}

/** Plain-language refusal from the server's envelope (blockers listed when present). */
function launchRefusalMessage(result: GovernedResult): string {
  const blockers = result.data?.blockers;
  if (Array.isArray(blockers) && blockers.length > 0) {
    const lines = blockers
      .map((b) => (b && typeof b === 'object' && typeof (b as { message?: unknown }).message === 'string'
        ? (b as { message: string }).message
        : null))
      .filter((m): m is string => !!m);
    if (lines.length > 0) return `${result.error ?? 'This design is not ready to launch.'} ${lines.join(' ')}`;
  }
  return result.error ?? 'Launch failed. Nothing was changed.';
}

/**
 * Launch the design: ONE governed server transaction (`launch_cycle_design`,
 * migration 749) writes the cycle, the participant set, every Track, every
 * Loop and its seed intent, and archives leftover tracks — or writes nothing.
 * A failure on the second path can no longer leave the first path's track and
 * loop behind. The stores are refreshed from the server afterwards.
 */
export async function launchCycleDesigner(
  input: LaunchCycleDesignerInput,
): Promise<LaunchCycleDesignerResult> {
  let originatorOrgUnitName: string | null = null;
  if (input.cycle.orgScopeNodeId) {
    try {
      const { useEnterpriseIdentityStore } = await import('@/stores/prioritization/enterpriseIdentityStore');
      originatorOrgUnitName =
        useEnterpriseIdentityStore.getState().orgUnits.get(input.cycle.orgScopeNodeId)?.displayName ?? null;
    } catch {
      originatorOrgUnitName = null;
    }
  }

  const built = buildLaunchConfig({ ...input, originatorOrgUnitName });
  if (!built.ok) return { ok: false, error: built.error, createdTrackIds: [] };

  const launch = input.launchDesign ?? launchCycleDesign;
  const result = await launch(input.cycle.id, built.config as unknown as Record<string, unknown>);
  if (!result.ok) {
    return { ok: false, error: launchRefusalMessage(result), createdTrackIds: [] };
  }

  const createdTrackIds = Array.isArray(result.data?.track_ids)
    ? (result.data.track_ids as unknown[]).filter((id): id is string => typeof id === 'string')
    : [];

  if (!input.launchDesign) await refreshAfterLaunch(input.cycle.id);

  return {
    ok: true,
    createdTrackIds,
    firstTrackId: createdTrackIds[0],
  };
}

/** Pull what the server wrote back into the stores. Best-effort: the launch already happened. */
async function refreshAfterLaunch(cycleId: string): Promise<void> {
  try {
    const [{ useDegapProcessStore }, { useCatchballLoopStore }, { useCatchballVirtualConfigStore }] = await Promise.all([
      import('@/stores/prioritization/degapProcessStore'),
      import('@/stores/prioritization/catchballLoopStore'),
      import('@/stores/prioritization/catchballVirtualConfigStore'),
    ]);
    // The tracks the launch made took the cycle's template seed on the server
    // as they were inserted (migration 1040); nothing is applied here.
    await Promise.all([
      useDegapProcessStore.getState().fetchCycleById(cycleId),
      useTrackStore.getState().fetchTracksForCycle(cycleId),
      useCatchballLoopStore.getState().fetchFromSupabase(cycleId),
      useCatchballVirtualConfigStore.getState().fetchForCycle(cycleId),
    ]);
  } catch (err) {
    console.warn('[launchCycleDesigner] Post-launch refresh failed (the launch itself succeeded):', err);
  }
}

/**
 * Launch lands on the cycle's own page (owner ruling 2026-11-03). It used to
 * land on the first path's track, but the cycle-wide settings live on the
 * cycle's page, and a cycle with several tracks has no reason to open on one of
 * them. The tracks are listed there, each one click away.
 */
export function buildDesignerLaunchUrl(cycleId: string): string {
  return `/prioritization/cycles/${encodeURIComponent(cycleId)}`;
}
